import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Typography
} from "@mui/material";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  ListResponseOfCertificateTemplatesOverrideExtended
} from "services/KnowfullyAdminClient";
import { ISnackbarMessage, Severity } from "types/common-types";
import CertificateTemplateProfessionForm from "./CertificateTemplateProfessionForm";
import CertificateTemplatesOverrideBox from "./CertificateTemplatesOverrideBox";

type CertificateTemplateProfessionsOverrideProps = {
  certificateTemplateId: string;
};

const CertificateTemplateProfessionsOverride = ({
  certificateTemplateId,
}: CertificateTemplateProfessionsOverrideProps) => {
  const { formatMessage: t } = useIntl();
  const [createOpen, createOpenActions] = useBoolean(false);
  const { certificateService } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<ISnackbarMessage | null>(null);

  const {
    data: certificateTemplatesOverrideData,
    isLoading: isLoadingCertificateTemplatesOverrides,
  } = useQuery<ListResponseOfCertificateTemplatesOverrideExtended>(
    ["certificateTemplatesOverrides", certificateTemplateId],
    () =>
      certificateService.getCertificateTemplatesOverrides(
        certificateTemplateId,
        null
      ),
    { enabled: !!certificateTemplateId }
  );

  const onSavedSuccessfully = () => {
    setSnackbarMessage({
      message: t({ id: "certificateTemplatesOverrideCreated" }),
      severity: Severity.Info,
    });
  };

  const onDeletedSuccessfully = () => {
    setSnackbarMessage({
      message: t({ id: "certificateTemplatesOverrideDeleted" }),
      severity: Severity.Warning,
    });
  };

  const onProfessionRepeated = () => {
    setSnackbarMessage({
      message: t({ id: "certificateTemplatesOverrideRepeated" }),
      severity: Severity.Error,
    });
  };

  return (
    <div>
      <div>
        <Box my={3}>
          <div>
            <Typography variant="h5" style={{ float: "left" }}>
              {t({ id: "certificateTemplateProfessionTemplateOverride" })}
            </Typography>
            <Button
              onClick={createOpenActions.setTrue}
              style={{ float: "right" }}
              data-cy="addOverrideButton"
            >
              {t({ id: "certificateTemplateAddProfession" })}
            </Button>
          </div>
        </Box>
      </div>
      <div>
        <hr style={{ width: "100%" }}></hr>
      </div>
      <Box my={2}>
        {isLoadingCertificateTemplatesOverrides ? (
          <CircularProgress size={32} />
        ) : (
          certificateTemplatesOverrideData?.data!.map((certificateTemplatesOverride) => (
            <div key={certificateTemplatesOverride.id!}>
              <CertificateTemplatesOverrideBox
                certificateTemplatesOverride={certificateTemplatesOverride}
                certificateTemplateId={certificateTemplateId}
                onSavedSuccessfully={onSavedSuccessfully}
                onDeletedSuccessfully={onDeletedSuccessfully}
                onProfessionRepeated={onProfessionRepeated}
              />
            </div>
          ))
        )}
      </Box>
      <Dialog open={createOpen} disableEnforceFocus maxWidth="md" fullWidth>
        <CertificateTemplateProfessionForm
          certificateTemplateId={certificateTemplateId}
          openActions={createOpenActions}
          onSavedSuccessfully={onSavedSuccessfully}
          onProfessionRepeated={onProfessionRepeated}
        />
      </Dialog>

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </div>
  );
};

export default CertificateTemplateProfessionsOverride;
